import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Row } from 'antd';

import type { RootState } from '@/redux/RootReducer';

import { ReactComponent as CartLateIcon } from '../../../../../assets/icons/cart-late.svg';
import { ReactComponent as CartWarningIcon } from '../../../../../assets/icons/cart-warning.svg';
import { ReactComponent as DashboardIcon } from '../../../../../assets/icons/dashboard-icon.svg';
import { ReactComponent as DocumentWarningIcon } from '../../../../../assets/icons/document-warning.svg';
import { ReactComponent as FilesIcon } from '../../../../../assets/icons/files-records.svg';
import { ReactComponent as LateDeliveryIcon } from '../../../../../assets/icons/late-delivery.svg';
import { ReactComponent as TrackingFailedIcon } from '../../../../../assets/icons/tracking-failed.svg';
import { setLossAndDamageFilters } from '../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import { routeFileSelector } from '../../../../../utilz/i18n';
import VerticalLine from '../../../../components/Bars/VerticalLine';
import IconButton from '../../../../components/Buttons/IconButton';
import PrimaryLabel from '../../../../components/Labels/PrimaryLabel';
import Drawer from '../../../../components/Modals/Drawer';
import FooterContentCard from '../../../common/FooterContentCard';
import { CLAIMS_FOOTER_TYPES } from '../../constants';
import type { ClaimsFooterType } from '../../types';
import LDClaimDashboard from '../Dashboard/LDClaimDashboard';
import { useGetClaimsList } from '../hooks';

const DEFAULT_FOOTER_DATA = {
  closedClaim: 0,
  filedAmount: 0,
  holdClaim: 0,
  openClaim: 0,
  paidAmount: 0,
  preClaim: 0,
  totalClaim: 0,
};

const LossAndDamageFooter: FC = () => {
  const [visible, setVisible] = useState(false);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const {
    filters,
    filterType,
    filters: { FooterStatus },
  } = useSelector((state: RootState) => state.lossAndDamage);

  const { data: claimsListData } = useGetClaimsList({
    filters,
    filterType,
  });

  const footerData = claimsListData?.footerData ?? DEFAULT_FOOTER_DATA;

  const dispatch = useDispatch();
  const handleFooterClick = (footerStatus: ClaimsFooterType) => {
    dispatch(setLossAndDamageFilters({ FooterStatus: footerStatus, pageNo: 1 }));
  };

  return (
    <div className="">
      <Row justify="space-around" className=" footer-container background-header px-20  py-12">
        <Col>
          <FooterContentCard
            count={String(footerData?.totalClaim ?? 0)}
            label="Total Claims"
            Icon={FilesIcon}
            onClick={() => handleFooterClick(CLAIMS_FOOTER_TYPES.ALL)}
            active={CLAIMS_FOOTER_TYPES.ALL === FooterStatus}
          />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard
            count={String(footerData?.openClaim ?? 0)}
            label="Open Claims"
            Icon={CartLateIcon}
            onClick={() => handleFooterClick(CLAIMS_FOOTER_TYPES.OPEN)}
            active={CLAIMS_FOOTER_TYPES.OPEN === FooterStatus}
          />
        </Col>

        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard
            count={String(footerData?.holdClaim ?? 0)}
            label="On Hold Claims"
            Icon={TrackingFailedIcon}
            onClick={() => handleFooterClick(CLAIMS_FOOTER_TYPES.HOLD)}
            active={CLAIMS_FOOTER_TYPES.HOLD === FooterStatus}
          />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard count={String(footerData?.preClaim ?? 0)} label="Pre Claims" Icon={TrackingFailedIcon} />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard
            count={String(footerData?.closedClaim ?? 0)}
            label="Closed Claims"
            Icon={LateDeliveryIcon}
            color="muted"
            textColor="text-muted"
          />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard
            count={
              <NumericFormat
                thousandsGroupStyle="thousand"
                displayType="text"
                value={String(footerData?.filedAmount ?? 0)}
                thousandSeparator
                fixedDecimalScale
                decimalScale={2}
              />
            }
            label="Total Amount Filed"
            Icon={DocumentWarningIcon}
          />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col>
          <FooterContentCard
            count={
              <NumericFormat
                thousandsGroupStyle="thousand"
                displayType="text"
                value={String(footerData?.paidAmount ?? 0)}
                thousandSeparator
                fixedDecimalScale
                decimalScale={2}
              />
            }
            label="Total Amount Paid"
            Icon={CartWarningIcon}
          />
        </Col>
        <Col>
          <VerticalLine />
        </Col>
        <Col className="d-flex">
          <IconButton onClick={() => setVisible(true)}>
            <DashboardIcon />
          </IconButton>
        </Col>
      </Row>
      <Drawer
        width="90%"
        open={visible}
        headerContents={
          <PrimaryLabel className="text-white">{t('translation:default.navMenu.dashboard')}</PrimaryLabel>
        }
        onClose={() => setVisible(false)}
      >
        <LDClaimDashboard />
      </Drawer>
    </div>
  );
};

export default LossAndDamageFooter;
